import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { FormatDate } from 'src/assets/functions/format-date';
import { CountryName } from 'src/assets/functions/get-country-name';
import { maskCpfAndCnpj, maskPhoneNumber, maskZipCode } from 'src/assets/functions/information-masks';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { ContractCancel } from '../../models/contract/contract-cancel';
import { ContractEdit } from '../../models/contract/contract-edit';
import { ContractEditCancel } from '../../models/contract/contract-edit-cancel';
import { FilterContractSearchResult } from '../../models/contract/filter-contract-search-result';
import { SalesTypeFordEnum } from '../../models/enum/sales-type-ford';
import { StatusContract } from '../../models/enum/status-contract';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ContractService } from '../../services/contract/contract.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';

type ValueChangeEvent<T> = { value: T };

@Component({
	selector: 'app-contract-edit',
	templateUrl: './contract-edit.component.html',
	styleUrls: ['./contract-edit.component.css'],
	standalone: false
})
export class ContractEditComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly searchContext = 'business' as const;

	pt = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
		dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
		dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
		monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
			'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
		monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
		today: 'Hoje',
		clear: 'Limpar'
	};

	es = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sábado'],
		dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
		dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
		monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
			'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
		monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
		today: 'Hoy',
		clear: 'Límpar'
	};

	calendarLang = this.pt;

	statusContract = [
		{ label: 'TRANSLATE.ACTIVE', value: StatusContract.ACTIVE },
		{ label: 'TRANSLATE.BLOCKED', value: StatusContract.BLOCKED },
		{ label: 'TRANSLATE.CANCELED', value: StatusContract.CANCELED },
		{ label: 'TRANSLATE.FINISHED', value: StatusContract.FINISHED },
		{ label: 'TRANSLATE.CREATED', value: StatusContract.CREATED },
		{ label: 'TRANSLATE.PENDING.PAYMENT', value: StatusContract.PENDING_PAYMENT },
		{ label: 'TRANSLATE.PENDING.CANCELLATION', value: StatusContract.PENDING_CANCELLATION },
		{ label: 'TRANSLATE.PENDING.ACTIVATION', value: StatusContract.PENDING_ACTIVATION },
		{ label: 'TRANSLATE.PENDING.AMENDMENT', value: StatusContract.PENDING_AMENDMENT },
		{ label: 'TRANSLATE.WITH.ERROR', value: StatusContract.WITH_ERROR }
	];

	filterContractSearchResult: FilterContractSearchResult;
	isPermissionButtonEditDisabled = true;
	isEditDisabled = true;
	isButtonSaveDisabled = true;
	isEditBlockDisabled = true;
	editableCancellationField = true;
	editedCancellationInformation = false;
	countCaracteresTextArea = 0;
	contractEdit!: ContractEdit;
	isSubmitted = false;
	hasAttemptedSave = false;
	blockedUi = false;
	isEditingMode = false;
	salesTypeFord: string = '';
	validateEmail = true;
	descriptionOptional: string = '';
	canceledContractInformation!: ContractCancel | null;
	cancellationDate!: Date | null;
	systemCountry: string = '';
	accordionActiveIndices: number[] = [0, 1, 2, 3, 4];
	cancelledTotalValueEdit: number | null = 0;
	cancelledFordValueEdit: number | null = 0;
	cancelledDealerValueEdit: number | null = 0;
	editedContractCancel: ContractEditCancel = {} as ContractEditCancel;
	totalValueObligatory = false;
	fordValueObligatory = false;
	dealerValueObligatory = false;
	isCancellationRecordMissing = false;
	isValidatingCancellationRecord = false;
	isResolvingEditEligibility = false;

	editingCustomerEmail: string = '';
	editingContractStatusCode: number = 0;
	editingWsdDate: Date | null = null;
	editingCoverageStartDate: Date | null = null;
	editingCoverageEndDate: Date | null = null;
	editingCoverageKm: number = 0;
	editingCoverageStartVisit: number = 0;
	editingCoverageVisitorCounter: number = 0;
	editingContractRemarks: string = '';
	contractCountryCode: string = '';
	requiredCustomerEmail = false;
	invalidEmailFormat = false;
	requiredContractStatusCode = false;
	requiredWsdDate = false;
	requiredCoverageStartDate = false;
	requiredCoverageEndDate = false;
	requiredCoverageStartVisit = false;
	requiredCoverageVisitorCounter = false;
	requiredContractNotes = false;
	editingContractInformation = false;

	private shouldApplyBrazilMasks(): boolean {
		return this.contractCountryCode === ConstantsFordProtect.BRA;
	}

	// Handlers for masked fields
	onDocumentInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.customerDocument = maskCpfAndCnpj(value || '');
		} else {
			this.filterContractSearchResult.customerDocument = value || '';
		}
	}

	onPhoneInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.customerPhone = maskPhoneNumber(value || '');
		} else {
			this.filterContractSearchResult.customerPhone = value || '';
		}
	}

	onCepInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.customerCep = maskZipCode(value || '');
		} else {
			this.filterContractSearchResult.customerCep = value || '';
		}
	}

	onDealerPhoneInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.dnPhone = maskPhoneNumber(value || '');
		} else {
			this.filterContractSearchResult.dnPhone = value || '';
		}
	}

	onSellerPhoneInput(value: string): void {
		if (this.shouldApplyBrazilMasks()) {
			this.filterContractSearchResult.sellerPhone = maskPhoneNumber(value || '');
		} else {
			this.filterContractSearchResult.sellerPhone = value || '';
		}
	}

	constructor(
		private translateService: TranslateService,
		private messageService: MessageService,
		private router: Router,
		private apsPermissionService: ApsPermissionService,
		private contractService: ContractService,
		private systemCountryService: SystemCountryService,
		private countryName: CountryName,
		private formatDate: FormatDate
	) {
		this.filterContractSearchResult = new FilterContractSearchResult();
		this.systemCountryService.setSystemCountry();
	}

	async ngOnInit() {

		window.scrollTo(0, 0);
		await this.apsPermissionService.bootstrapPermissions();

		this.systemCountry = this.systemCountryService.getSystemCountry();
		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenContractView);
		this.isPermissionButtonEditDisabled = this.apsPermissionService.isPermissionValidationDisabled(ApsPermitedScreen.screenContractEdit);
		this.searchContract();

		this.updateCalendarLanguage(this.translateService.currentLang || this.translateService.defaultLang || ConstantsFordProtect.PT_BR);

		this.translateService.onLangChange.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe((langChange: LangChangeEvent) => {
			this.updateCalendarLanguage(langChange.lang);
		});

	}

	private updateCalendarLanguage(language: string): void {
		this.calendarLang = language.toLowerCase().startsWith('es') ? this.es : this.pt;
		this.searchSalesTypeFord();
	}

	private searchContract() {

		const savedSearchResult = this.contractService.getFilterContractSearchResultSimplifiedByContext(this.searchContext);

		if (savedSearchResult !== null && savedSearchResult !== undefined) {

			const contractId = savedSearchResult.contractKey;
			const productId = savedSearchResult.productKey;
			const coverageCategoryId = savedSearchResult.coverageCategoryKey;
			const vin = savedSearchResult.vin;

			this.searchContractId(contractId, productId, coverageCategoryId, vin);
		}
	}

	private convertDateNeverTimezone(date: Date): Date {
		return new Date(date + 'T00:00:00.00');
	}

	textAreaCounterCaracteres() {

		if (this.editingContractRemarks === null || this.editingContractRemarks === '') {
			this.countCaracteresTextArea = 0;
		}
		else {
			this.countCaracteresTextArea = this.editingContractRemarks.length;
		}
	}

	buttonComeBack() {
		this.router.navigate(['contract-search'], { state: { restoreSearch: true, source: 'contract-edit' } });
		this.canceledContractInformation = new ContractCancel();
	}

	buttonEditarEnabled() {
		if (this.isCanceledContract()) {
			this.showCanceledContractCannotBeEditedMessage();
			return;
		}

		if (this.isCanceledContractWithoutEditableCancellation()) {
			this.showCanceledContractNotEditableMessage();
			return;
		}

		this.isEditingMode = true;
		this.hasAttemptedSave = false;
		this.clearRequiredFields();
		this.totalValueObligatory = false;
		this.fordValueObligatory = false;
		this.dealerValueObligatory = false;

		this.isEditDisabled = false;
		this.isButtonSaveDisabled = false;

		if (this.filterContractSearchResult.productCode !== ConstantsFordProtect.PREB &&
			this.filterContractSearchResult.contractStatusCode === StatusContract.PENDING_CANCELLATION &&
			this.canceledContractInformation?.cancelledTotalValue != null &&
			this.canceledContractInformation?.cancelledFordValue != null &&
			this.canceledContractInformation?.cancelledDealerValue != null &&
			Object.keys(this.canceledContractInformation ?? {}).length > 0
		) {

			this.editableCancellationField = false;
		} else {

			this.editableCancellationField = true;
		}
	}

	buttonEditDisabled() {
		this.isEditingMode = false;
		this.hasAttemptedSave = false;
		this.isEditDisabled = true;
		this.isButtonSaveDisabled = true;
		this.editableCancellationField = true;
		this.editingContractInformation = false;
		this.editedCancellationInformation = false;
		this.clearRequiredFields();
		this.totalValueObligatory = false;
		this.fordValueObligatory = false;
		this.dealerValueObligatory = false;
		this.searchContract();
	}

	get isEditActionDisabledByCancellationState(): boolean {
		return this.isResolvingEditEligibility || this.isCanceledContract() || this.isCanceledContractWithoutEditableCancellation();
	}

	get editDisabledReasonKey(): string {
		if (this.isResolvingEditEligibility) {
			return '';
		}

		if (this.isCanceledContract()) {
			return 'TRANSLATE.CONTRACT.CANCELED.CANNOT.BE.EDITED';
		}

		if (this.isCanceledContractWithoutEditableCancellation()) {
			return 'TRANSLATE.CONTRACT.CANCELED.WITHOUT.EDITABLE.CANCELLATION';
		}

		return '';
	}

	private isCanceledContract(): boolean {
		return this.filterContractSearchResult?.contractStatusCode === StatusContract.CANCELED;
	}

	private isCanceledContractWithoutEditableCancellation(): boolean {
		return this.filterContractSearchResult.contractStatusCode === StatusContract.CANCELED
			&& this.isCancellationRecordMissing;
	}

	private showCanceledContractNotEditableMessage(): void {
		this.messageService.add({
			severity: 'warn',
			summary: this.translateService.instant('TRANSLATE.ALERT'),
			detail: this.translateService.instant('TRANSLATE.CONTRACT.CANCELED.WITHOUT.EDITABLE.CANCELLATION')
		});
	}

	private showCanceledContractCannotBeEditedMessage(): void {
		this.messageService.add({
			severity: 'warn',
			summary: this.translateService.instant('TRANSLATE.ALERT'),
			detail: this.translateService.instant('TRANSLATE.CONTRACT.CANCELED.CANNOT.BE.EDITED')
		});
	}

	private isMissingContractVinCancelError(err: unknown): boolean {
		const error = err as {
			status?: number;
			error?: { exceptionMessage?: string; message?: string };
			message?: string;
		};

		const message = `${error?.error?.exceptionMessage ?? ''} ${error?.error?.message ?? ''} ${error?.message ?? ''}`.toLowerCase();
		const hasExplicitContractVinCancelSignal =
			message.includes('contractvincancelnotfoundexception') || message.includes('contractvincancel');

		const hasCanceledContextWithoutEditableRecord =
			error?.status === 404
			&& this.isCancellationRecordMissing
			&& this.filterContractSearchResult?.productCode !== ConstantsFordProtect.PREB
			&& (
				this.filterContractSearchResult?.contractStatusCode === StatusContract.CANCELED
				|| this.filterContractSearchResult?.contractStatusCode === StatusContract.PENDING_CANCELLATION
			);

		return error?.status === 404 && (hasExplicitContractVinCancelSignal || hasCanceledContextWithoutEditableRecord);
	}

	checkCoverageStartVisit() {
		// Validate digit limit first
		this.validateCoverageStartVisitDigits(this.editingCoverageStartVisit);

		if (!this.hasAttemptedSave) {
			return;
		}

		this.requiredCoverageStartVisit = this.isRequiredCounterFieldEmpty(this.editingCoverageStartVisit);
	}

	checkCoverageVisitorCounter() {
		// Validate digit limit first
		this.validateCoverageVisitorCounterDigits(this.editingCoverageVisitorCounter);

		if (!this.hasAttemptedSave) {
			return;
		}

		this.requiredCoverageVisitorCounter = this.isRequiredCounterFieldEmpty(this.editingCoverageVisitorCounter);
	}

	validateCoverageStartVisitDigits(value: number | null | undefined): void {
		if (value === null || value === undefined) {
			return;
		}
		// Limit to maximum 99 (2 digits)
		if (value > 99) {
			this.editingCoverageStartVisit = 99;
		}
	}

	validateCoverageVisitorCounterDigits(value: number | null | undefined): void {
		if (value === null || value === undefined) {
			return;
		}
		// Limit to maximum 999999999999 (12 digits)
		if (value > 999999999999) {
			this.editingCoverageVisitorCounter = 999999999999;
		}
	}

	limitCoverageStartVisitInput(event: Event): void {
		const inputElement = event.target as HTMLInputElement;
		if (!inputElement) return;

		// Extract only digits
		let digitsOnly = inputElement.value.replace(/[^0-9]/g, '');

		// Limit to 2 digits maximum (value 99)
		if (digitsOnly.length > 2) {
			digitsOnly = digitsOnly.substring(0, 2);
		}

		// Update input value
		inputElement.value = digitsOnly;

		// Update ngModel
		this.editingCoverageStartVisit = digitsOnly ? parseInt(digitsOnly, 10) : 0;
	}

	limitCoverageVisitorCounterInput(event: Event): void {
		const inputElement = event.target as HTMLInputElement;
		if (!inputElement) return;

		// Extract only digits
		let digitsOnly = inputElement.value.replace(/[^0-9]/g, '');

		// Limit to 12 digits maximum (value 999999999999)
		if (digitsOnly.length > 12) {
			digitsOnly = digitsOnly.substring(0, 12);
		}

		// Update input value
		inputElement.value = digitsOnly;

		// Update ngModel
		this.editingCoverageVisitorCounter = digitsOnly ? parseInt(digitsOnly, 10) : 0;
	}

	restrictCoverageStartVisitKeypress(event: KeyboardEvent): void {
		const inputElement = event.target as HTMLInputElement;
		if (!inputElement) return;

		// Allow: backspace, delete, tab, escape, enter
		if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
			// Allow: Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A
			(event.keyCode === 65 && event.ctrlKey === true) ||
			(event.keyCode === 67 && event.ctrlKey === true) ||
			(event.keyCode === 86 && event.ctrlKey === true) ||
			(event.keyCode === 88 && event.ctrlKey === true)) {
			return;
		}

		// Only allow digits (0-9)
		if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
			event.preventDefault();
			return;
		}

		// Extract only digits from current value
		const currentDigits = inputElement.value.replace(/[^0-9]/g, '');

		// If already has 2 digits (max for 99), prevent additional digits
		if (currentDigits.length >= 2) {
			event.preventDefault();
			return;
		}
	}

	restrictCoverageVisitorCounterKeypress(event: KeyboardEvent): void {
		const inputElement = event.target as HTMLInputElement;
		if (!inputElement) return;

		// Allow: backspace, delete, tab, escape, enter
		if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
			// Allow: Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A
			(event.keyCode === 65 && event.ctrlKey === true) ||
			(event.keyCode === 67 && event.ctrlKey === true) ||
			(event.keyCode === 86 && event.ctrlKey === true) ||
			(event.keyCode === 88 && event.ctrlKey === true)) {
			return;
		}

		// Only allow digits (0-9)
		if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
			event.preventDefault();
			return;
		}

		// Extract only digits from current value
		const currentDigits = inputElement.value.replace(/[^0-9]/g, '');

		// If already has 12 digits (max for 999999999999), prevent additional digits
		if (currentDigits.length >= 12) {
			event.preventDefault();
			return;
		}
	}





	private isValidEmailFormat(email: string): boolean {
		if (!email || email.trim().length === 0) {
			return false;
		}
		// RFC 5322 simplified email pattern
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailPattern.test(email.trim());
	}

	onCustomerEmailInput(value: string): void {
		this.editingCustomerEmail = value ?? '';

		if (this.hasAttemptedSave) {
			this.requiredCustomerEmail = this.editingCustomerEmail.trim().length === 0;
		}
	}

	onContractStatusChange(event: ValueChangeEvent<number | null | undefined>): void {
		const status = event?.value;
		this.editingContractStatusCode = status ? Number(status) : 0;

		if (this.hasAttemptedSave) {
			this.requiredContractStatusCode = !this.editingContractStatusCode;
		}
	}

	onWsdDateChange(value: Date | null): void {
		if (this.hasAttemptedSave) {
			this.requiredWsdDate = value === null || value === undefined;
		}
	}

	onCoverageStartDateChange(value: Date | null): void {
		if (this.hasAttemptedSave) {
			this.requiredCoverageStartDate = value === null || value === undefined;
		}
	}

	onCoverageEndDateChange(value: Date | null): void {
		if (this.hasAttemptedSave) {
			this.requiredCoverageEndDate = value === null || value === undefined;
		}
	}

	onContractRemarksInput(value: string): void {
		this.editingContractRemarks = value ?? '';
		this.textAreaCounterCaracteres();

		if (this.hasAttemptedSave) {
			this.requiredContractNotes = this.editingContractRemarks.trim().length === 0;
		}
	}

	onCancelledTotalValueChange(value: number | null): void {
		this.cancelledTotalValueEdit = value;

		if (this.hasAttemptedSave) {
			this.totalValueObligatory = value === null || value === undefined;
		}
	}

	onCancelledFordValueChange(value: number | null): void {
		this.cancelledFordValueEdit = value;

		if (this.hasAttemptedSave) {
			this.fordValueObligatory = value === null || value === undefined;
		}
	}

	onCancelledDealerValueChange(value: number | null): void {
		this.cancelledDealerValueEdit = value;

		if (this.hasAttemptedSave) {
			this.dealerValueObligatory = value === null || value === undefined;
		}
	}

	buttonSaveEdit() {
		this.hasAttemptedSave = true;

		if (this.isCanceledContract()) {
			this.showCanceledContractCannotBeEditedMessage();
			return;
		}

		if (this.isCanceledContractWithoutEditableCancellation()) {
			this.showCanceledContractNotEditableMessage();
			return;
		}

		// Validate email format before proceeding
		if (this.isEditingMode && this.editingCustomerEmail && !this.isValidEmailFormat(this.editingCustomerEmail)) {
			this.invalidEmailFormat = true;
			this.focusFirstInvalidRequiredField();
			return;
		}

		this.invalidEmailFormat = false;
		this.requiredCoverageStartVisit = this.isRequiredCounterFieldEmpty(this.editingCoverageStartVisit);
		this.requiredCoverageVisitorCounter = this.isRequiredCounterFieldEmpty(this.editingCoverageVisitorCounter);

		if (this.filterContractSearchResult.productCode !== ConstantsFordProtect.PREB) {

			if (this.canceledContractInformation === null || this.canceledContractInformation === undefined) {

				if (
					this.editingCustomerEmail === null || this.editingCustomerEmail === undefined ||
					this.editingCustomerEmail.trim().length === 0 || this.editingCustomerEmail === '' ||
					this.editingContractStatusCode === null || this.editingContractStatusCode === undefined ||
					this.filterContractSearchResult.wsd === null || this.filterContractSearchResult.wsd === undefined ||
					this.filterContractSearchResult.coverageStartDate === null ||
					this.filterContractSearchResult.coverageStartDate === undefined ||
					this.filterContractSearchResult.coverageEndDate === null ||
					this.filterContractSearchResult.coverageEndDate === undefined ||
					this.requiredCoverageStartVisit ||
					this.requiredCoverageVisitorCounter ||
					this.editingContractRemarks === null || this.editingContractRemarks === undefined ||
					this.editingContractRemarks.trim().length === 0
				) {

					this.requiredCustomerEmail = this.editingCustomerEmail === null || this.editingCustomerEmail === undefined ||
						this.editingCustomerEmail.trim().length === 0 || this.editingCustomerEmail === '';
					this.requiredContractStatusCode = this.editingContractStatusCode === null || this.editingContractStatusCode === undefined;
					this.requiredWsdDate = this.filterContractSearchResult.wsd === null ||
						this.filterContractSearchResult.wsd === undefined;
					this.requiredCoverageStartDate = this.filterContractSearchResult.coverageStartDate === null ||
						this.filterContractSearchResult.coverageStartDate === undefined;
					this.requiredCoverageEndDate = this.filterContractSearchResult.coverageEndDate === null ||
						this.filterContractSearchResult.coverageEndDate === undefined;
					this.requiredCoverageStartVisit = this.isRequiredCounterFieldEmpty(this.editingCoverageStartVisit);
					this.requiredCoverageVisitorCounter = this.isRequiredCounterFieldEmpty(this.editingCoverageVisitorCounter);
					this.requiredContractNotes = this.editingContractRemarks === null || this.editingContractRemarks === undefined ||
						this.editingContractRemarks.trim().length === 0;
					this.focusFirstInvalidRequiredField();
					return;
				} else {

					this.clearRequiredFields();

					// ensures that the date from the frontend matches the one from the database
					this.editingWsdDate = this.formatDate.formatDate(this.filterContractSearchResult.wsd);
					this.editingCoverageStartDate = this.formatDate.formatDate(this.filterContractSearchResult.coverageStartDate);
					this.editingCoverageEndDate = this.formatDate.formatDate(this.filterContractSearchResult.coverageEndDate);

					this.editingContractInformation =
						this.editingCustomerEmail !== this.filterContractSearchResult.customerEmail ||
						this.editingContractStatusCode !== this.filterContractSearchResult.contractStatusCode ||
						this.editingWsdDate !== this.filterContractSearchResult.wsd ||
						this.editingCoverageStartDate !== this.filterContractSearchResult.coverageStartDate ||
						this.editingCoverageEndDate !== this.filterContractSearchResult.coverageEndDate ||
						this.editingCoverageKm !== this.filterContractSearchResult.coverageKm ||
						this.editingCoverageStartVisit !== this.filterContractSearchResult.coverageStartVisit ||
						this.editingCoverageVisitorCounter !== this.filterContractSearchResult.coverageVisitCounter ||
						this.editingContractRemarks !== this.filterContractSearchResult.contractRemarks;

					this.savingContractChanges(this.createObjectContractEdit());
				}

			} else {

				if (
					this.editingCustomerEmail === null || this.editingCustomerEmail === undefined ||
					this.editingCustomerEmail.trim().length === 0 || this.editingCustomerEmail === '' ||
					this.editingContractStatusCode === null || this.editingContractStatusCode === undefined ||
					this.filterContractSearchResult.wsd === null || this.filterContractSearchResult.wsd === undefined ||
					this.filterContractSearchResult.coverageStartDate === null ||
					this.filterContractSearchResult.coverageStartDate === undefined ||
					this.filterContractSearchResult.coverageEndDate === null ||
					this.filterContractSearchResult.coverageEndDate === undefined ||
					this.requiredCoverageStartVisit ||
					this.requiredCoverageVisitorCounter ||
					this.editingContractRemarks === null || this.editingContractRemarks === undefined ||
					this.editingContractRemarks.trim().length === 0
				) {

					this.requiredCustomerEmail = this.editingCustomerEmail === null || this.editingCustomerEmail === undefined ||
						this.editingCustomerEmail.trim().length === 0 || this.editingCustomerEmail === '';
					this.requiredContractStatusCode = this.editingContractStatusCode === null || this.editingContractStatusCode === undefined;
					this.requiredWsdDate = this.filterContractSearchResult.wsd === null || this.filterContractSearchResult.wsd === undefined;
					this.requiredCoverageStartDate = this.editingCoverageStartDate === null || this.editingCoverageStartDate === undefined;
					this.requiredCoverageEndDate = this.filterContractSearchResult.coverageEndDate === null ||
						this.filterContractSearchResult.coverageEndDate === undefined;
					this.requiredCoverageStartVisit = this.isRequiredCounterFieldEmpty(this.editingCoverageStartVisit);
					this.requiredCoverageVisitorCounter = this.isRequiredCounterFieldEmpty(this.editingCoverageVisitorCounter);
					this.requiredContractNotes = this.editingContractRemarks === null || this.editingContractRemarks === undefined ||
						this.editingContractRemarks.trim().length === 0;
					this.focusFirstInvalidRequiredField();
					return;
				}
				if (
					this.cancelledTotalValueEdit === null || this.cancelledTotalValueEdit === undefined ||
					this.cancelledFordValueEdit === null || this.cancelledFordValueEdit === undefined ||
					this.cancelledDealerValueEdit === null || this.cancelledDealerValueEdit === undefined
				) {

					this.totalValueObligatory = this.cancelledTotalValueEdit === null || this.cancelledTotalValueEdit === undefined;
					this.fordValueObligatory = this.cancelledFordValueEdit === null || this.cancelledFordValueEdit === undefined;
					this.dealerValueObligatory = this.cancelledDealerValueEdit === null || this.cancelledDealerValueEdit === undefined;
					this.focusFirstInvalidRequiredField();
					return;
				} else {

					this.clearRequiredFields();

					// ensures that the date from the frontend matches the one from the database
					this.editingWsdDate = this.formatDate.formatDate(this.filterContractSearchResult.wsd);
					this.editingCoverageStartDate = this.formatDate.formatDate(this.filterContractSearchResult.coverageStartDate);
					this.editingCoverageEndDate = this.formatDate.formatDate(this.filterContractSearchResult.coverageEndDate);

					this.editingContractInformation =
						this.editingCustomerEmail !== this.filterContractSearchResult.customerEmail ||
						this.editingContractStatusCode !== this.filterContractSearchResult.contractStatusCode ||
						this.editingWsdDate !== this.filterContractSearchResult.wsd ||
						this.editingCoverageStartDate !== this.filterContractSearchResult.coverageStartDate ||
						this.editingCoverageEndDate !== this.filterContractSearchResult.coverageEndDate ||
						this.editingCoverageKm !== this.filterContractSearchResult.coverageKm ||
						this.editingCoverageStartVisit !== this.filterContractSearchResult.coverageStartVisit ||
						this.editingCoverageVisitorCounter !== this.filterContractSearchResult.coverageVisitCounter ||
						this.editingContractRemarks !== this.filterContractSearchResult.contractRemarks;

					this.editedCancellationInformation =
						this.cancelledTotalValueEdit !== this.canceledContractInformation.cancelledTotalValue ||
						this.cancelledFordValueEdit !== this.canceledContractInformation.cancelledFordValue ||
						this.cancelledDealerValueEdit !== this.canceledContractInformation.cancelledDealerValue;

					if (this.editingContractInformation || this.editedCancellationInformation) {

						// If both were changed, call both services and only show a final message
						if (this.editingContractInformation && this.editedCancellationInformation) {

							this.isSubmitted = true;
							this.blockedUi = true;

							this.contractEdit = this.createObjectContractEdit();
							this.contractEdit.country = this.countryName.getCodeFromName(this.filterContractSearchResult.country);
							this.contractEdit.email = this.editingCustomerEmail;
							this.contractEdit.contractStatusCode = this.editingContractStatusCode;
							this.contractEdit.cvvgDate = this.filterContractSearchResult.wsd;
							this.contractEdit.coverageStartDate = this.filterContractSearchResult.coverageStartDate;
							this.contractEdit.coverageEndDate = this.filterContractSearchResult.coverageEndDate;
							this.contractEdit.coverageKm = this.editingCoverageKm;
							this.contractEdit.coverageStartVisit = this.editingCoverageStartVisit;
							this.contractEdit.coverageVisitCounter = this.editingCoverageVisitorCounter;
							this.contractEdit.contractRemarks = this.editingContractRemarks;
							this.contractEdit.cancelledTotalValue = this.cancelledTotalValueEdit;

							this.editedContractCancel.contractId = this.canceledContractInformation.contractId;
							this.editedContractCancel.vin = this.canceledContractInformation.vin;
							this.editedContractCancel.cancelledTotalValue = this.cancelledTotalValueEdit;
							this.editedContractCancel.cancelledFordValue = this.cancelledFordValueEdit;
							this.editedContractCancel.cancelledDealerValue = this.cancelledDealerValueEdit;
							this.editedContractCancel.productId = this.contractEdit.productKey;

							// Create observables for the service calls
							const contractEdit$ = this.contractService.contractEditPost(this.contractEdit);
							const cancelEdit$ = this.contractService.updateContractCancel(this.editedContractCancel);

							// Execute both in parallel and handle the combined result
							forkJoin([contractEdit$, cancelEdit$]).pipe(
								takeUntilDestroyed(this.destroyRef)
							).subscribe(
								([contractResponse, cancelResponse]) => {

									let success = true;
									if (contractResponse !== true) success = false;
									if (!cancelResponse) success = false;

									if (success) {
										this.messageService.add({
											severity: 'success',
											summary: this.translateService.instant('TRANSLATE.SUCCESS'),
											detail: this.translateService.instant('TRANSLATE.CONTRACT.AND.CANCELLATION.SUCCESSFULLY.SAVED')
										});

										this.searchContract();
										this.blockedUi = false;
										this.isSubmitted = false;
										this.buttonEditDisabled();
										this.canceledContractInformation = new ContractCancel();
									}

								}, (err) => {
									if (this.isMissingContractVinCancelError(err)) {
										this.messageService.add({
											severity: 'warn',
											summary: this.translateService.instant('TRANSLATE.ALERT'),
											detail: this.translateService.instant('TRANSLATE.CANNOT.SAVE.CONTRACT.CANCELED.WITHOUT.EDITABLE.CANCELLATION')
										});
										this.isSubmitted = false;
										this.blockedUi = false;
										return;
									}

									switch (err.status) {

										case 400:
										case 404:
										case 500:
											this.messageService.add({
												severity: 'error',
												summary: this.translateService.instant('TRANSLATE.ERROR'),
												detail: this.translateService.instant('TRANSLATE.ERROR.SAVING.CONTRACT.AND.CANCELLATION')
											});
											break;

										case 401:
											this.messageService.add({
												severity: 'error',
												summary: this.translateService.instant('TRANSLATE.ERROR'),
												detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
											});
											break;

										default:
											this.messageService.add({
												severity: 'error',
												summary: this.translateService.instant('TRANSLATE.ERROR'),
												detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
											});
											break;
									}

									this.isSubmitted = false;
									this.blockedUi = false;
								}
							);

						} else if (this.editingContractInformation) {

							this.savingContractChanges(this.createObjectContractEdit());
						} else if (this.editedCancellationInformation) {

							this.editCancellationInformation(this.editedContractCancel);
						}

					}

				}

			}

		} else {

			if (
				this.editingCustomerEmail === null || this.editingCustomerEmail === undefined ||
				this.editingCustomerEmail.trim().length === 0 || this.editingCustomerEmail === '' ||
				this.editingContractStatusCode === null || this.editingContractStatusCode === undefined ||
				this.filterContractSearchResult.wsd === null || this.filterContractSearchResult.wsd === undefined ||
				this.filterContractSearchResult.coverageStartDate === null ||
				this.filterContractSearchResult.coverageStartDate === undefined ||
				this.filterContractSearchResult.coverageEndDate === null ||
				this.filterContractSearchResult.coverageEndDate === undefined ||
				this.requiredCoverageStartVisit ||
				this.requiredCoverageVisitorCounter ||
				this.editingContractRemarks === null || this.editingContractRemarks === undefined ||
				this.editingContractRemarks.trim().length === 0
			) {

				this.requiredCustomerEmail = this.editingCustomerEmail === null || this.editingCustomerEmail === undefined ||
					this.editingCustomerEmail.trim().length === 0 || this.editingCustomerEmail === '';
				this.requiredContractStatusCode = this.editingContractStatusCode === null || this.editingContractStatusCode === undefined;
				this.requiredWsdDate = this.filterContractSearchResult.wsd === null ||
					this.filterContractSearchResult.wsd === undefined;
				this.requiredCoverageStartDate = this.filterContractSearchResult.coverageStartDate === null ||
					this.filterContractSearchResult.coverageStartDate === undefined;
				this.requiredCoverageEndDate = this.filterContractSearchResult.coverageEndDate === null ||
					this.filterContractSearchResult.coverageEndDate === undefined;
				this.requiredCoverageStartVisit = this.isRequiredCounterFieldEmpty(this.editingCoverageStartVisit);
				this.requiredCoverageVisitorCounter = this.isRequiredCounterFieldEmpty(this.editingCoverageVisitorCounter);
				this.requiredContractNotes = this.editingContractRemarks === null || this.editingContractRemarks === undefined ||
					this.editingContractRemarks.trim().length === 0;
				this.focusFirstInvalidRequiredField();
				return;
			} else {

				this.clearRequiredFields();

				// ensures that the date from the frontend matches the one from the database
				this.editingWsdDate = this.formatDate.formatDate(this.filterContractSearchResult.wsd);
				this.editingCoverageStartDate = this.formatDate.formatDate(this.filterContractSearchResult.coverageStartDate);
				this.editingCoverageEndDate = this.formatDate.formatDate(this.filterContractSearchResult.coverageEndDate);

				this.editingContractInformation =
					this.editingCustomerEmail !== this.filterContractSearchResult.customerEmail ||
					this.editingContractStatusCode !== this.filterContractSearchResult.contractStatusCode ||
					this.editingWsdDate !== this.filterContractSearchResult.wsd ||
					this.editingCoverageStartDate !== this.filterContractSearchResult.coverageStartDate ||
					this.editingCoverageEndDate !== this.filterContractSearchResult.coverageEndDate ||
					this.editingCoverageKm !== this.filterContractSearchResult.coverageKm ||
					this.editingCoverageStartVisit !== this.filterContractSearchResult.coverageStartVisit ||
					this.editingCoverageVisitorCounter !== this.filterContractSearchResult.coverageVisitCounter ||
					this.editingContractRemarks !== this.filterContractSearchResult.contractRemarks;

				this.savingContractChanges(this.createObjectContractEdit());
			}

		}

	}

	private focusFirstInvalidRequiredField(): void {
		const invalidFieldIds: string[] = [];

		if (this.invalidEmailFormat) invalidFieldIds.push('customerEmailEdit');
		if (this.requiredCustomerEmail) invalidFieldIds.push('customerEmailEdit');
		if (this.requiredContractStatusCode) invalidFieldIds.push('contractStatusEdit');
		if (this.requiredWsdDate) invalidFieldIds.push('wsdDateEdit');
		if (this.requiredCoverageStartDate) invalidFieldIds.push('coverageStartDateEdit');
		if (this.requiredCoverageEndDate) invalidFieldIds.push('coverageEndDateEdit');
		if (this.requiredCoverageStartVisit) invalidFieldIds.push('coverageStartVisit');
		if (this.requiredCoverageVisitorCounter) invalidFieldIds.push('coverageVisitorCounter');
		if (this.totalValueObligatory) invalidFieldIds.push('finalPriceReturn');
		if (this.fordValueObligatory) invalidFieldIds.push('fordPriceReturn');
		if (this.dealerValueObligatory) invalidFieldIds.push('dealerPriceReturn');
		if (this.requiredContractNotes) invalidFieldIds.push('editingContractRemarks');

		if (!invalidFieldIds.length) {
			return;
		}

		this.accordionActiveIndices = [0, 1, 2, 3, 4];

		setTimeout(() => {
			const firstInvalidField = this.getFocusableField(invalidFieldIds[0]);
			if (!firstInvalidField) {
				return;
			}
			firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
			setTimeout(() => firstInvalidField.focus(), 150);
		}, 300);
	}

	private isRequiredCounterFieldEmpty(value: number | string | null | undefined): boolean {
		if (value === null || value === undefined) {
			return true;
		}

		if (typeof value === 'string') {
			return value.trim().length === 0;
		}

		return false;
	}

	private getFocusableField(fieldId: string): HTMLElement | null {
		const fieldElement = document.getElementById(fieldId);
		if (!fieldElement) {
			return null;
		}

		if (fieldElement instanceof HTMLInputElement || fieldElement instanceof HTMLTextAreaElement || fieldElement.tabIndex >= 0) {
			return fieldElement;
		}

		const nestedFocusable = fieldElement.querySelector<HTMLElement>('input, textarea, [tabindex]');
		return nestedFocusable ?? fieldElement;
	}

	savingContractChanges(contractEdit: ContractEdit) {

		this.isSubmitted = true;
		this.blockedUi = true;
		contractEdit.country = this.countryName.getCodeFromName(this.filterContractSearchResult.country);
		contractEdit.email = this.editingCustomerEmail;
		contractEdit.contractStatusCode = this.editingContractStatusCode;
		contractEdit.cvvgDate = this.filterContractSearchResult.wsd;
		contractEdit.coverageStartDate = this.filterContractSearchResult.coverageStartDate;
		contractEdit.coverageEndDate = this.filterContractSearchResult.coverageEndDate;
		contractEdit.coverageKm = this.editingCoverageKm;
		contractEdit.coverageStartVisit = this.editingCoverageStartVisit;
		contractEdit.coverageVisitCounter = this.editingCoverageVisitorCounter;
		contractEdit.contractRemarks = this.editingContractRemarks;

		console.log(this.cancelledTotalValueEdit);
		if (this.cancelledTotalValueEdit == undefined) {
			this.cancelledTotalValueEdit = 0;
		}
		contractEdit.cancelledTotalValue = this.cancelledTotalValueEdit;

		this.contractService.contractEditPost(contractEdit)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: boolean) => {

				if (response) {

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.CONTRACT.SUCCESSFULLY.SAVED')
					});

					this.searchContract();
					this.blockedUi = false;
					this.isSubmitted = false;
					this.buttonEditDisabled();
				}

			}, (err) => {

				if (this.isMissingContractVinCancelError(err)) {
					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ALERT'),
						detail: this.translateService.instant('TRANSLATE.CANNOT.SAVE.CONTRACT.CANCELED.WITHOUT.EDITABLE.CANCELLATION')
					});
					this.isSubmitted = false;
					this.blockedUi = false;
					return;
				}

				switch (err.status) {

					case 400:
					case 404:
					case 500:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.CONTRACT.CANNOT.BE.SAVE.CONTACT.ADM')
						});
						break;

					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

				this.isSubmitted = false;
				this.blockedUi = false;
			});

	}

	editCancellationInformation(editedContractCancel: ContractEditCancel) {

		this.isSubmitted = true;
		this.blockedUi = true;
		editedContractCancel.contractId = this.canceledContractInformation?.contractId ?? 0;
		editedContractCancel.vin = this.canceledContractInformation?.vin ?? '';
		editedContractCancel.cancelledTotalValue = this.cancelledTotalValueEdit ?? 0;
		editedContractCancel.cancelledFordValue = this.cancelledFordValueEdit ?? 0;
		editedContractCancel.cancelledDealerValue = this.cancelledDealerValueEdit ?? 0;

		this.contractService.updateContractCancel(editedContractCancel)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: ContractCancel) => {

				if (response) {

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.CANCELLATION.INFORMATION.SAVED.SUCCESSFULLY')
					});

					this.searchContract();
					this.blockedUi = false;
					this.isSubmitted = false;
					this.buttonEditDisabled();
				}

			}, (err) => {

				if (this.isMissingContractVinCancelError(err)) {
					this.messageService.add({
						severity: 'warn',
						summary: this.translateService.instant('TRANSLATE.ALERT'),
						detail: this.translateService.instant('TRANSLATE.CANNOT.SAVE.CONTRACT.CANCELED.WITHOUT.EDITABLE.CANCELLATION')
					});
					this.isSubmitted = false;
					this.blockedUi = false;
					return;
				}

				switch (err.status) {

					case 400:
					case 404:
					case 500:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.CANCELLATION.INFORMATION.CANNOT.BE.SAVED')
						});
						break;

					case 401:

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;

					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

				this.isSubmitted = false;
				this.blockedUi = false;
			});

	}

	private createObjectContractEdit(): ContractEdit {

		this.contractEdit = new ContractEdit();
		this.contractEdit.csId = this.apsPermissionService.getUserCsid();
		this.contractEdit.contractCodeFp2 = this.filterContractSearchResult.contractCodeFp2;
		this.contractEdit.contractRemarks = this.filterContractSearchResult.contractRemarks;
		this.contractEdit.contractStatusCode = this.filterContractSearchResult.contractStatusCode;
		this.contractEdit.country = this.filterContractSearchResult.country;
		this.contractEdit.coverageCategory = this.filterContractSearchResult.coverageCategory;
		this.contractEdit.coverageCode = this.filterContractSearchResult.coverageCode;
		this.contractEdit.cvvgDate = this.filterContractSearchResult.wsd;
		this.contractEdit.coverageStartDate = this.filterContractSearchResult.coverageStartDate;
		this.contractEdit.coverageEndDate = this.filterContractSearchResult.coverageEndDate;
		this.contractEdit.operation = this.filterContractSearchResult.operation;
		this.contractEdit.vin = this.filterContractSearchResult.vin;
		this.contractEdit.vinKey = this.filterContractSearchResult.vinKey;
		this.contractEdit.coverageKey = this.filterContractSearchResult.coverageKey;
		this.contractEdit.productCode = this.filterContractSearchResult.productCode;
		this.contractEdit.productKey = this.filterContractSearchResult.productKey;
		this.contractEdit.email = this.filterContractSearchResult.customerEmail;

		if (this.filterContractSearchResult.coverageStartVisit >= 1) {
			this.contractEdit.coverageStartVisit = this.filterContractSearchResult.coverageStartVisit;
		}

		if (this.filterContractSearchResult.coverageKm !== null && this.filterContractSearchResult.coverageKm >= 0) {
			this.contractEdit.coverageKm = this.filterContractSearchResult.coverageKm;
		}

		if (this.filterContractSearchResult.coverageVisitCounter !== null && this.filterContractSearchResult.coverageVisitCounter >= 0) {
			this.contractEdit.coverageVisitCounter = this.filterContractSearchResult.coverageVisitCounter;
		}

		return this.contractEdit;
	}

	clearRequiredFields() {

		this.requiredCustomerEmail = false;
		this.requiredContractStatusCode = false;
		this.requiredWsdDate = false;
		this.requiredCoverageStartDate = false;
		this.requiredCoverageEndDate = false;
		this.requiredCoverageStartVisit = false;
		this.requiredCoverageVisitorCounter = false;
		this.requiredContractNotes = false;
	}

	private searchContractId(contractId: number, productId: number, coverageCategoryId: number, vin: string) {
		this.isCancellationRecordMissing = false;
		this.isValidatingCancellationRecord = false;
		this.isResolvingEditEligibility = true;

		this.contractService.searchContractId(contractId, productId, coverageCategoryId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: FilterContractSearchResult) => {

				this.filterContractSearchResult = response;
				this.contractCountryCode = response.country || '';
				// Apply masks to loaded data only for Brazil (BRA)
				if (this.shouldApplyBrazilMasks()) {
					this.filterContractSearchResult.customerDocument = maskCpfAndCnpj(this.filterContractSearchResult.customerDocument || '');
					this.filterContractSearchResult.customerPhone = maskPhoneNumber(this.filterContractSearchResult.customerPhone || '');
					this.filterContractSearchResult.customerCep = maskZipCode(this.filterContractSearchResult.customerCep || '');
					this.filterContractSearchResult.dnPhone = maskPhoneNumber(this.filterContractSearchResult.dnPhone || '');
					this.filterContractSearchResult.sellerPhone = maskPhoneNumber(this.filterContractSearchResult.sellerPhone || '');
				}

				this.filterContractSearchResult.country = this.countryName.getCountryName(this.filterContractSearchResult.country);
				this.editingCustomerEmail = this.filterContractSearchResult.customerEmail;
				this.editingContractStatusCode = this.filterContractSearchResult.contractStatusCode;
				this.editingWsdDate = this.filterContractSearchResult.wsd;
				this.editingCoverageStartDate = this.filterContractSearchResult.coverageStartDate;
				this.editingCoverageEndDate = this.filterContractSearchResult.coverageEndDate;

				this.editingCoverageKm = this.filterContractSearchResult.coverageKm;
				this.editingCoverageStartVisit = this.filterContractSearchResult.coverageStartVisit;
				this.editingCoverageVisitorCounter = this.filterContractSearchResult.coverageVisitCounter;
				this.editingContractRemarks = this.filterContractSearchResult.contractRemarks;

				this.textAreaCounterCaracteres()
				this.validationDate();
				this.searchSalesTypeFord();

				if (this.filterContractSearchResult.productCode !== ConstantsFordProtect.PREB &&
					(this.filterContractSearchResult.contractStatusCode === StatusContract.CANCELED ||
						this.filterContractSearchResult.contractStatusCode === StatusContract.PENDING_CANCELLATION)) {

					this.isValidatingCancellationRecord = true;
					console.log(`Status do contrato é ${this.filterContractSearchResult.status}. Retornando o cancelamento do contrato`);
					this.getContractVinCancel(contractId, vin);
				} else {
					this.isResolvingEditEligibility = false;
				}
			}, (err) => {

				this.isValidatingCancellationRecord = false;
				this.isResolvingEditEligibility = false;

				switch (err.status) {

					case 400:
					case 404:
					case 500:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.INVALID.CONTRACT.SEARCH.CONTACT.ADM')
						});
						break;
					case 401:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});
						break;
					default:
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
						break;
				}

			});
	}

	private getContractVinCancel(contractId: number, vin: string) {

		this.contractService.getContractVinCancel(contractId, vin)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: ContractCancel) => {

				this.canceledContractInformation = response;

				if (this.canceledContractInformation && Object.keys(this.canceledContractInformation).length > 0) {

					this.cancellationDate = this.filterContractSearchResult.vinActualEndDate;
					this.cancelledTotalValueEdit = this.canceledContractInformation.cancelledTotalValue;
					this.cancelledFordValueEdit = this.canceledContractInformation.cancelledFordValue;
					this.cancelledDealerValueEdit = this.canceledContractInformation.cancelledDealerValue;
				} else {
					// No result found, just clear the related fields
					this.cancellationDate = null;
					this.cancelledTotalValueEdit = null;
					this.cancelledFordValueEdit = null;
					this.cancelledDealerValueEdit = null;
				}

				this.isValidatingCancellationRecord = false;
				this.isResolvingEditEligibility = false;

			}, (err) => {

				if (err.status === 404) {
					this.isCancellationRecordMissing = true;
					this.canceledContractInformation = null;
					this.cancellationDate = null;
					this.cancelledTotalValueEdit = null;
					this.cancelledFordValueEdit = null;
					this.cancelledDealerValueEdit = null;
					this.isValidatingCancellationRecord = false;
					this.isResolvingEditEligibility = false;
					return;
				}

				this.isValidatingCancellationRecord = false;
				this.isResolvingEditEligibility = false;
			});

	}

	private validationDate() {
		if (this.filterContractSearchResult.contractDateCreate !== null) {
			this.filterContractSearchResult.contractDateCreate = this.convertDateNeverTimezone(this.filterContractSearchResult.contractDateCreate);
		}

		if (this.filterContractSearchResult.contractDateEnd !== null) {
			this.filterContractSearchResult.contractDateEnd = this.convertDateNeverTimezone(this.filterContractSearchResult.contractDateEnd);
		}

		if (this.filterContractSearchResult.contractDateUpdate !== null) {
			this.filterContractSearchResult.contractDateUpdate = this.convertDateNeverTimezone(this.filterContractSearchResult.contractDateUpdate);
		}

		if (this.filterContractSearchResult.wsd !== null) {
			this.filterContractSearchResult.wsd = this.convertDateNeverTimezone(this.filterContractSearchResult.wsd);
		}

		if (this.filterContractSearchResult.warrantyEndDate !== null) {
			this.filterContractSearchResult.warrantyEndDate = this.convertDateNeverTimezone(this.filterContractSearchResult.warrantyEndDate);
		}

		if (this.filterContractSearchResult.vinUpdateDate !== null) {
			this.filterContractSearchResult.vinUpdateDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinUpdateDate);
		}

		if (this.filterContractSearchResult.vinSaleDate !== null) {
			this.filterContractSearchResult.vinSaleDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinSaleDate);
		}

		if (this.filterContractSearchResult.vinStartDate !== null) {
			this.filterContractSearchResult.vinStartDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinStartDate);
		}

		if (this.filterContractSearchResult.vinEndDate !== null) {
			this.filterContractSearchResult.vinEndDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinEndDate);
		}

		if (this.filterContractSearchResult.coverageStartDate !== null) {
			this.filterContractSearchResult.coverageStartDate = this.convertDateNeverTimezone(this.filterContractSearchResult.coverageStartDate);
		}

		if (this.filterContractSearchResult.coverageEndDate !== null) {
			this.filterContractSearchResult.coverageEndDate = this.convertDateNeverTimezone(this.filterContractSearchResult.coverageEndDate);
		}

		if (this.filterContractSearchResult.coverageUpdateDate !== null) {
			this.filterContractSearchResult.coverageUpdateDate = this.convertDateNeverTimezone(this.filterContractSearchResult.coverageUpdateDate);
		}

		if (this.filterContractSearchResult.vinActualEndDate !== null) {
			this.filterContractSearchResult.vinActualEndDate = this.convertDateNeverTimezone(this.filterContractSearchResult.vinActualEndDate);
		}
	}

	rangeDateLabel(): string {

		const date = new Date();
		const dateLabelYear = date.getFullYear() + 50;
		return '1970:' + dateLabelYear;
	}

	searchSalesTypeFord() {
		this.salesTypeFord = this.translateSalesTypeFords(String(this.filterContractSearchResult.contractTypeCode));
	}

	private translateSalesTypeFords(saleType: string) {

		switch (saleType) {

			case SalesTypeFordEnum.RETAIL_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.RETAIL');
				break;

			case SalesTypeFordEnum.DIRECT_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.DIRECT');
				break;

			case SalesTypeFordEnum.GOVERNMENT_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.FLEET');
				break;

			case SalesTypeFordEnum.FREE_CONTRACT_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.FREE.CONTRACT');
				break;

			case SalesTypeFordEnum.FORD_FLEET_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.INTERNAL.FLEET');
				break;

			case SalesTypeFordEnum.FORD_GO_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.INTERNAL.FORDGO');
				break;

			case SalesTypeFordEnum.TELEMARKETING_DESCRIPTION:
				saleType = this.translateService.instant('TRANSLATE.INTERNAL.TELEMARKETING');
				break;
		}

		return saleType;
	}

	private isEmail(search: string): boolean {

		const regexp = new RegExp(/^(([^<>()[]\\.,;:\s@"]+(\.[^<>()[]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/);
		return regexp.test(search);
	}

	validateEditForm(): boolean {
		let isValid = true;

		if (this.isEmail(this.filterContractSearchResult.customerEmail) === false) {

			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.INVALID.EMAIL.PLEASE.FILL.IN.CORRECTLY')
			});

			isValid = false;
		}
		else if (this.filterContractSearchResult.contractRemarks === "" || this.filterContractSearchResult.contractRemarks === null) {

			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.CONTRACT.REMARKS.FIELD.REQUIRED.PLEASE.CORRECTLY'),
			});

			isValid = false;
		}

		return isValid;
	}

}
